import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import {
  fetchIntelligencePageData,
  fetchReportBySlug,
  fetchReportsPageData,
  ReportsPageData,
} from "content-ui/intelligence/fetcher";
import {
  fetchNavbarAndFooter,
  fetchPaywallByIdServer,
} from "lib/fetchers/common";

import ReportsPageView from "content-ui/intelligence/reportsPageView";
import type { Report } from "content-ui/intelligence/types";
import { set } from "lodash";
import { makeDecisionServer } from "lib/zephr/public-api";
import { DecisionAction } from "lib/zephr/types";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";
import { PaywallWindow } from "lib/types/paywalls";

export async function getServerSideProps({ req, params, preview = false }) {
  const featuredReport = await fetchReportBySlug(params.slug, preview);

  const blaizeSession = req.cookies["blaize_session"] || "";
  const decision = await makeDecisionServer(
    blaizeSession,
    "reports-sdk",
    {
      is_pro: featuredReport?.membershipLevel?.toLowerCase() === "pro",
      is_free: featuredReport?.membershipLevel?.toLowerCase() === "free",
      is_member: featuredReport?.membershipLevel?.toLowerCase() === "member",
    },
    req
  );

  if (decision?.action !== DecisionAction.NONE && featuredReport) {
    set(featuredReport, "pdf", "");
  }

  const [menuItems, pageData, dato, paywall] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchIntelligencePageData({ preview }),
    fetchReportsPageData({ preview }),
    decision?.action === DecisionAction.DISPLAY_PAYWALL
      ? fetchPaywallByIdServer(decision?.payload?.paywallId)
      : null,
  ]);

  const reportsList = pageData.reports
    .filter((report) => {
      return report.title !== featuredReport?.title;
    }, this)
    .map((report) => set(report, "pdf", ""));

  return {
    notFound: !featuredReport,
    props: {
      paywall,
      dato,
      featuredReport,
      reportsList,
      pageData,
      canonical: buildCanonicalUrl(`/intelligence/reports/${params.slug}`),
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}
type ReportsPageProps = {
  dato: ReportsPageData;
  featuredReport: Report;
  reportsList: Report[];
  featuredReportSubtitle: string;
  paywall: PaywallWindow | null;
  canonical: string;
};

const ReportsPage = (props: ReportsPageProps) => {
  if (!props?.featuredReport) return null;

  const title = props?.featuredReport?.title + " | Sifted";
  const description = props?.featuredReport?.description;
  const seoImage = props?.featuredReport?.image;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: props.canonical,
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      <ErrorBoundary>
        <>
          {props.paywall !== null && (
            <ClientOnlyPaywall paywall={props.paywall} />
          )}
          <ReportsPageView {...props} />
        </>
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default ReportsPage;
